import styled from '@emotion/styled'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { ClickAwayListener, Stack, Tooltip, Typography } from '@mui/material'
import { TwinResponderThreadDraftStatus } from 'api/model'
import { COLORS } from 'app-constants'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useState } from 'react'

const LabelStack = styled(Stack)`
  padding: 5px;
  border-radius: 4px;
  background-color: #f5f5f5;
  justify-content: center;
  align-items: center;
  width: fit-content;
`

export const LabelTags = ({ labels }: { labels: string[] }) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Tooltip title={labels.join(', ')} enterTouchDelay={0}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mx={2}
        maxHeight="32px"
        flex="0 0 100px"
        my={isMobile ? 2 : 0}
        justifyContent="flex-start">
        {labels.slice(0, 1).map((label) => {
          return <LabelTag key={label} labelName={label} />
        })}
        {labels.length > 1 && (
          <LabelStack>
            <Typography
              variant="h7"
              sx={{
                color: COLORS.GRAY_3,
              }}>
              +{labels.length - 1}
            </Typography>
          </LabelStack>
        )}
      </Stack>
    </Tooltip>
  )
}

export const LabelTag = ({ labelName }: { labelName: string }) => {
  // update the labelName to not have the Category_ prefix
  if (labelName.startsWith('CATEGORY_')) {
    labelName = labelName.substring(9)
  }

  return (
    <LabelStack>
      <Typography
        variant="h8"
        sx={{
          color: COLORS.GRAY_3,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}>
        {labelName}
      </Typography>
    </LabelStack>
  )
}

export const getSkipReasonText = (reason?: string) => {
  let skipReasonText = reason ?? ''

  switch (reason) {
    case 'unsubscribe_message':
      skipReasonText = 'because the email was a promotional email.'
      break
    case 'no_reply_message':
      skipReasonText = 'because the email was an automated notification.'
      break
    case 'already_replied':
      skipReasonText = 'because you have already replied to the thread.'
      break
    case 'no_reasons_given':
      skipReasonText = 'because there is not enough data.'
      break
    case 'jelled_support_message':
      skipReasonText = 'because the email is an interaction with Jelled.'
      break
    case 'newsletter':
      skipReasonText = 'because the email is a newsletter.'
      break
    case 'automated notification':
      skipReasonText = 'because the email was an automated notification.'
      break
    case 'sales outreach':
      skipReasonText = 'because the email was a sales outreach.'
      break
    default:
      skipReasonText = 'because the email is obviously does not need to be responded to.'
      break
  }

  return skipReasonText
}

export const DraftStatusBadge = ({
  reason,
  draftStatus,
}: {
  reason?: string
  draftStatus: TwinResponderThreadDraftStatus
}) => {
  const [isSkippedTooltipOpen, setIsSkippedTooltipOpen] = useState<boolean>(false)

  switch (draftStatus) {
    case TwinResponderThreadDraftStatus.drafted:
      return (
        <Stack
          px={2}
          py={1}
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="center"
          borderRadius="4px"
          width="80px"
          flex="0 0 80px"
          maxHeight="32px"
          sx={{
            backgroundColor: 'success.main',
          }}>
          <Typography variant="h8" color="#FFFFFF">
            DRAFTED
          </Typography>
        </Stack>
      )
    case TwinResponderThreadDraftStatus.not_drafted:
      return (
        <ClickAwayListener
          onClickAway={() => {
            setIsSkippedTooltipOpen(false)
          }}>
          <Tooltip
            enterTouchDelay={0}
            onMouseEnter={() => setIsSkippedTooltipOpen(true)}
            onMouseLeave={() => setIsSkippedTooltipOpen(false)}
            open={isSkippedTooltipOpen}
            title={`A draft was not generated for this email ${getSkipReasonText(
              reason
            )} You can force a draft by going into the email and clicking "Auto-draft a response".`}>
            <Stack
              px={1}
              py={1}
              direction="row"
              alignItems="center"
              spacing="4px"
              justifyContent="center"
              border="1px solid #222222"
              width="80px"
              flex="0 0 80px"
              borderRadius="4px"
              maxHeight="32px"
              onClick={(e) => {
                setIsSkippedTooltipOpen(!isSkippedTooltipOpen)
                e.preventDefault()
                e.stopPropagation()
              }}
              sx={{
                backgroundColor: COLORS.GRAY_8,
              }}>
              <InfoOutlined
                sx={{
                  color: COLORS.GRAY_3,
                  height: '16px',
                  width: '16px',
                }}
              />
              <Typography variant="h8" color="secondary">
                SKIPPED
              </Typography>
            </Stack>
          </Tooltip>
        </ClickAwayListener>
      )
    case TwinResponderThreadDraftStatus.sent:
      return (
        <Stack
          px={2}
          py={1}
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="center"
          borderRadius="4px"
          maxHeight="32px"
          width="80px"
          flex="0 0 80px"
          sx={{
            backgroundColor: 'primary.main',
          }}>
          <Typography
            variant="h8"
            sx={{
              color: COLORS.PURE_WHITE,
            }}>
            SENT
          </Typography>
        </Stack>
      )
  }
}
